import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAnimation } from '../contexts/AnimationContext';
import api, { formatApiError, isAuthenticated, getTenantSubdomain } from '../services/api';
import './Login.css';

const Register = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { useTransformersAnimation, useAnimatePresence } = useAnimation();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('EMPLOYE');
  const [team, setTeam] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const firstNameInputRef = useRef(null);
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const authAccent = isRsTenant ? '#2a2a2a' : '#0045F5';

  // Redirect authenticated users to admin dashboard
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    setIsSuccess(false);

    try {
      const userData = {
        email,
        first_name: firstName,
        last_name: lastName,
        role,
        // Employees are not assigned teams during registration - managers will assign them later
        team_id: null,
      };

      await api.register(userData);
      
      // Show success message
      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      const formattedError = formatApiError(err);
      setFieldErrors(formattedError.fieldErrors);
      setError(formattedError.generalError);
    } finally {
      setIsLoading(false);
    }
  };

  // Team field is not shown for any role during registration
  // Employees will be assigned to teams by managers after registration
  // #region agent log
  // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Register.js:showTeamField-calculation',message:'Role state before showTeamField calculation',data:{role:role,currentLogic:'always false - employees assigned by managers',hypothesisId:'A'},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix'})}).catch(()=>{});
  // #endregion agent log
  const showTeamField = false; // Employees are assigned to teams by managers, not during registration
  // #region agent log
  // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Register.js:showTeamField-result',message:'showTeamField calculated value',data:{role:role,showTeamField:showTeamField,note:'Employees assigned by managers after registration',hypothesisId:'B'},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix'})}).catch(()=>{});
  // #endregion agent log

  // Focus on first name input after animation completes (only for transformers animation)
  useEffect(() => {
    if (useTransformersAnimation) {
      const timer = setTimeout(() => {
        if (firstNameInputRef.current) {
          firstNameInputRef.current.focus();
        }
      }, 600); // Wait for form container animation (0.5s) + small buffer

      return () => clearTimeout(timer);
    }
  }, [useTransformersAnimation]);

  // Conditional AnimatePresence wrapper
  const ConditionalAnimatePresence = ({ children }) => {
    return useAnimatePresence ? (
      <AnimatePresence>{children}</AnimatePresence>
    ) : (
      <>{children}</>
    );
  };

  // Render form container based on animation mode
  const FormContainer = useTransformersAnimation ? motion.div : 'div';
  const formContainerProps = useTransformersAnimation ? {
    layoutId: "form-container",
    layout: "size",
    initial: { y: 0 },
    animate: { y: 0 },
    style: { willChange: 'height' }
  } : {};

  // Render header based on animation mode
  const HeaderContainer = useTransformersAnimation ? motion.div : 'div';
  const headerProps = useTransformersAnimation ? {
    layoutId: "header",
    layout: false,
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3, ease: 'easeInOut' },
    style: { willChange: 'opacity' }
  } : {};

  // Render input wrapper based on animation mode
  const InputWrapper = useTransformersAnimation ? motion.div : 'div';
  const getInputWrapperProps = (fieldName, delay, isRegisterSpecific = false) => {
    if (!useTransformersAnimation) return {};
    
    if (isRegisterSpecific) {
      // For register-specific fields (name, confirm password, terms), use scale animation
      return {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
        transition: { duration: 0.35, delay, ease: 'easeOut' },
        style: { willChange: 'transform, opacity' }
      };
    }
    
    // For shared fields (email, password), use layoutId with opacity animation (no layout translation)
    return {
      layoutId: `${fieldName}-input`,
      layout: false,
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.3, delay, ease: 'easeInOut' },
      style: { willChange: 'opacity' }
    };
  };

  // Render button based on animation mode
  const ButtonWrapper = useTransformersAnimation ? motion.button : 'button';
  const buttonProps = useTransformersAnimation ? {
    layoutId: "submit-button",
    layout: false,
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0 },
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
      mass: 0.8,
      delay: 0.25
    },
    style: { 
      background: authAccent,
      willChange: 'transform, opacity' 
    }
  } : {
    style: { background: authAccent }
  };

  return (
    <>
      <main className="relative z-10 w-full px-0 py-0 md:px-4 py-0 md:max-w-[800px] h-screen md:h-auto">
        <div className="relative p-[2px] rounded-none md:rounded-2xl transition-all duration-500 w-full h-full md:h-auto transform group/card scale-100 md:scale-[0.8]" style={{ background: authAccent, transformOrigin: 'center center' }}>
          <FormContainer className="glass-card rounded-none md:rounded-2xl p-6 md:p-10 w-full h-full relative z-10 flex flex-col overflow-y-auto" {...formContainerProps}>
            <HeaderContainer className="text-center mb-2" {...headerProps}>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">{t('registerTitle')}</h1>
              <p className="text-gray-500 text-base">{t('registerSubtitle')}</p>
            </HeaderContainer>

            {/* Success message */}
            {isSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="material-symbols-outlined text-green-600">check_circle</span>
                  <div>
                    <p className="text-sm font-semibold text-green-900 mb-1">{t('accountCreatedTitle')}</p>
                    <p className="text-xs text-green-700">{t('accountCreatedBody')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && !isSuccess && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-6 flex-grow">
              {/* Email */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('email', 0.05)}>
                <label 
                  className="block text-sm font-semibold text-gray-700 mb-2" 
                  htmlFor="email"
                >
                  {tCommon('email')} <span className="text-red-500">*</span>
                </label>
                <input
                  className={`w-full px-4 py-3 border-2 ${
                    fieldErrors.email ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 placeholder-gray-400`}
                  id="email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email) {
                      setFieldErrors({ ...fieldErrors, email: null });
                    }
                  }}
                  required
                  disabled={isLoading || isSuccess}
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.email[0]}</p>
                )}
              </InputWrapper>

              {/* Name Fields Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('firstName', 0.1, true)}>
                  <label 
                    className="block text-sm font-semibold text-gray-700 mb-2" 
                    htmlFor="first_name"
                  >
                    {t('firstName')}
                  </label>
                  <input
                    ref={firstNameInputRef}
                    className={`w-full px-4 py-3 border-2 ${
                      fieldErrors.first_name ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 placeholder-gray-400`}
                    id="first_name"
                    type="text"
                    name="first_name"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (fieldErrors.first_name) {
                        setFieldErrors({ ...fieldErrors, first_name: null });
                      }
                    }}
                    disabled={isLoading || isSuccess}
                  />
                  {fieldErrors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.first_name[0]}</p>
                  )}
                </InputWrapper>
                <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('lastName', 0.15, true)}>
                  <label 
                    className="block text-sm font-semibold text-gray-700 mb-2" 
                    htmlFor="last_name"
                  >
                    {t('lastName')}
                  </label>
                  <input
                    className={`w-full px-4 py-3 border-2 ${
                      fieldErrors.last_name ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 placeholder-gray-400`}
                    id="last_name"
                    type="text"
                    name="last_name"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (fieldErrors.last_name) {
                        setFieldErrors({ ...fieldErrors, last_name: null });
                      }
                    }}
                    disabled={isLoading || isSuccess}
                  />
                  {fieldErrors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.last_name[0]}</p>
                  )}
                </InputWrapper>
              </div>

              {/* Info Message */}
              <ConditionalAnimatePresence>
                {useTransformersAnimation ? (
                  <motion.div 
                    className="bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50 dark:text-blue-200 rounded-lg p-4"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, delay: 0.2, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-blue-900 mb-1">{t('automaticPasswordTitle')}</p>
                        <p className="text-xs text-blue-700">{t('automaticPasswordBody')}</p>
                        {role === 'EMPLOYE' && (
                          <p className="text-xs text-blue-700 mt-2">{t('employeesTeamNote')}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:border-blue-700/50 dark:text-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-blue-900 mb-1">{t('automaticPasswordTitle')}</p>
                        <p className="text-xs text-blue-700">{t('automaticPasswordBody')}</p>
                        {role === 'EMPLOYE' && (
                          <p className="text-xs text-blue-700 mt-2">{t('employeesTeamNote')}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </ConditionalAnimatePresence>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent md:bg-white text-gray-500">{t('professionalInformation')}</span>
                </div>
              </div>

              {/* Role */}
              <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('role', 0.25, true)}>
                <label 
                  className="block text-sm font-semibold text-gray-700 mb-2" 
                  htmlFor="role"
                >
                  {t('role')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="role"
                    name="role"
                  value={role}
                  onChange={(e) => {
                    // #region agent log
                    // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Register.js:role-onChange',message:'Role changed by user',data:{oldRole:role,newRole:e.target.value,hypothesisId:'C'},timestamp:Date.now(),sessionId:'debug-session',runId:'initial'})}).catch(()=>{});
                    // #endregion agent log
                    setRole(e.target.value);
                    // Clear team field when switching to MANAGER or ADMIN role
                    if (e.target.value === 'MANAGER' || e.target.value === 'ADMIN') {
                      setTeam('');
                    }
                    if (fieldErrors.role) {
                      setFieldErrors({ ...fieldErrors, role: null });
                    }
                  }}
                  required
                    className={`w-full px-4 py-3 pr-10 border-2 ${
                      fieldErrors.role ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 bg-white appearance-none cursor-pointer`}
                    disabled={isLoading || isSuccess}
                  >
                    <option value="EMPLOYE">{t('roleEmployee')}</option>
                    <option value="MANAGER">{t('roleManager')}</option>
                    <option value="ADMIN">{t('roleAdmin')}</option>
                  </select>
                  <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">arrow_drop_down</span>
                </div>
                {fieldErrors.role && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.role[0]}</p>
                )}
              </InputWrapper>

              {/* Team Field (conditionally shown) */}
              {/* #region agent log */}
              {(() => {
                // fetch('http://127.0.0.1:7246/ingest/ef873a82-9c52-4e5a-9883-1a70679064d1',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Register.js:team-field-render',message:'Team field render decision',data:{role:role,showTeamField:showTeamField,willRender:showTeamField,hypothesisId:'D'},timestamp:Date.now(),sessionId:'debug-session',runId:'initial'})}).catch(()=>{});
                return null;
              })()}
              {/* #endregion agent log */}
              <ConditionalAnimatePresence>
                {showTeamField && (
                  useTransformersAnimation ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="transition-all duration-300"
                    >
                      <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('team', 0.3, true)}>
                        <label 
                          className="block text-sm font-semibold text-gray-700 mb-2" 
                          htmlFor="team"
                        >
                          {t('team')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <select
                            id="team"
                            name="team"
                            value={team}
                            onChange={(e) => {
                              setTeam(e.target.value);
                              if (fieldErrors.team_id) {
                                setFieldErrors({ ...fieldErrors, team_id: null });
                              }
                            }}
                            required={showTeamField}
                            className={`w-full px-4 py-3 pr-10 border-2 ${
                              fieldErrors.team_id ? 'border-red-500' : 'border-gray-300'
                            } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 bg-white appearance-none cursor-pointer`}
                            disabled={isLoading || isSuccess}
                          >
                          <option value="">{t('selectTeam')}</option>
                          <option value="3">Équipe Support (Centre Lyon)</option>
                          <option value="4">Équipe Ventes (Centre Lyon)</option>
                          <option value="5">Équipe RH (Centre Marseille)</option>
                          <option value="1">Équipe Développement (Centre Paris)</option>
                          <option value="2">Équipe Marketing (Centre Paris)</option>
                          <option value="6">Test Team (Test Center)</option>
                          </select>
                          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">arrow_drop_down</span>
                        </div>
                        {fieldErrors.team_id && (
                          <p className="mt-1 text-sm text-red-600">{fieldErrors.team_id[0]}</p>
                        )}
                      </InputWrapper>
                    </motion.div>
                  ) : (
                    <div className="transition-all duration-300">
                      <InputWrapper className="group relative transition-all duration-300" {...getInputWrapperProps('team', 0.3, true)}>
                        <label 
                          className="block text-sm font-semibold text-gray-700 mb-2" 
                          htmlFor="team"
                        >
                          {t('team')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <select
                            id="team"
                            name="team"
                            value={team}
                            onChange={(e) => {
                              setTeam(e.target.value);
                              if (fieldErrors.team_id) {
                                setFieldErrors({ ...fieldErrors, team_id: null });
                              }
                            }}
                            required={showTeamField}
                            className={`w-full px-4 py-3 pr-10 border-2 ${
                              fieldErrors.team_id ? 'border-red-500' : 'border-gray-300'
                            } rounded-lg focus:outline-none focus:ring-2 focus:ring-nokia-blue focus:border-transparent transition-all duration-200 bg-white appearance-none cursor-pointer`}
                            disabled={isLoading || isSuccess}
                          >
                          <option value="">{t('selectTeam')}</option>
                          <option value="3">Équipe Support (Centre Lyon)</option>
                          <option value="4">Équipe Ventes (Centre Lyon)</option>
                          <option value="5">Équipe RH (Centre Marseille)</option>
                          <option value="1">Équipe Développement (Centre Paris)</option>
                          <option value="2">Équipe Marketing (Centre Paris)</option>
                          <option value="6">Test Team (Test Center)</option>
                          </select>
                          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">arrow_drop_down</span>
                        </div>
                        {fieldErrors.team_id && (
                          <p className="mt-1 text-sm text-red-600">{fieldErrors.team_id[0]}</p>
                        )}
                      </InputWrapper>
                    </div>
                  )
                )}
              </ConditionalAnimatePresence>

              {/* Register button */}
              <ButtonWrapper
                className={`relative overflow-hidden w-full text-white font-bold rounded-lg py-3 md:py-4 px-4 md:px-6 mt-6 transition-all duration-300 transform active:scale-[0.98] group border border-transparent ${
                  isLoading || isSuccess ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                {...buttonProps}
                type="submit"
                disabled={isLoading || isSuccess}
              >
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                <span className="relative z-10 flex items-center justify-center gap-2 tracking-wide text-sm md:text-base">
                  {isSuccess ? (
                    <>
                      <span className="material-symbols-outlined">check_circle</span>
                      {t('accountCreatedCta')}
                    </>
                  ) : (
                    t('createAccountCta')
                  )}
                </span>
              </ButtonWrapper>
            </form>

            {/* Login link */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-base">
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-nokia-blue font-bold hover:text-primary-hover transition-all">
                  {t('signIn')}
                </Link>
              </p>
            </div>
          </FormContainer>

          {/* Decorative corners */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-nokia-blue rounded-tl-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-nokia-green rounded-br-2xl opacity-40 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        </div>
      </main>
    </>
  );
};

export default Register;

